import type {
  SubscriptionPlanType,
  SubscriptionTierType,
} from '@repo/subscription/api';

import { type SupportedCurrency } from './SubscriptionPrice';

export type InfoIconModalContent = {
  title: string;
  content: string;
};

/** @knipignore - used in website */
export type PricingPlanComparisonItem =
  | { label: string | ((value?: number) => string) }
  | { check: boolean }
  | { infoIconModalContent: InfoIconModalContent };

export interface PricingPlanComparison {
  features: ReadonlyArray<PricingPlanComparisonItem>;
  aiDesignTools: ReadonlyArray<PricingPlanComparisonItem>;
  licensing: ReadonlyArray<PricingPlanComparisonItem>;
  content: ReadonlyArray<PricingPlanComparisonItem>;
  businessAddons: ReadonlyArray<
    PricingPlanComparisonItem | { contactSales: boolean }
  >;
}

export type StripePriceId = `price_${string}`;
type StripeProductId = `prod_${string}`;

export interface MonetaryValue {
  // The amount should ALWAYS be in the smallest possible unit of a currency.
  amount: number;
  currency: SupportedCurrency;
}

interface StripePrice extends MonetaryValue {
  stripeId?: StripePriceId;
}

export interface PricingPlanAddon extends StripePrice {
  generationCredits: number;
}

export type PricingPlanValue = MonetaryValue & {
  id: string;
  addons?: ReadonlyArray<PricingPlanAddon>;
};

export type PricingPlanPrice = { label?: string } & {
  [K in 'monthly' | 'yearly']: PricingPlanValue;
};

export interface PricingPlanFeature {
  label: string;
  bold?: boolean;
  details?: string;
  isGenerationCredits?: boolean;
  premium?: boolean;
  infoIconModalContent?: InfoIconModalContent;
}

export interface PricingPlan {
  title: string;
  description: string | JSX.Element;
  buttonLabel: string;
  productId?: StripeProductId;
  price: PricingPlanPrice;
  featuresLabel: string;
  mainFeatures: ReadonlyArray<PricingPlanFeature>;
  otherFeatures?: ReadonlyArray<PricingPlanFeature>;
  comparison: PricingPlanComparison;
  background?: string;
}

export type PricingConfig = {
  [key in SubscriptionTierType]: PricingPlan;
};

export type CreditLimits = Record<SubscriptionPlanType, number>;

export enum PricingIntervals {
  Monthly = 'monthly',
  Yearly = 'yearly',
}
